/**
 * Módulo de UI para el sistema de carga de Excel
 * Maneja indicadores visuales, mensajes de progreso y errores
 */

const LoaderUI = {
    elements: {},
    isInitialized: false,

    /**
     * Inicializa los elementos de UI
     */
    init() {
        if (this.isInitialized) return;

        // Crear contenedor principal si no existe
        if (!document.getElementById('excel-loader-ui')) {
            const container = document.createElement('div');
            container.id = 'excel-loader-ui';
            container.className = 'excel-loader-container';
            container.innerHTML = `
                <div class="excel-loader-overlay"></div>
                <div class="excel-loader-content">
                    <div class="excel-loader-spinner"></div>
                    <div class="excel-loader-title">Cargando Base de Datos</div>
                    <div class="excel-loader-message">Iniciando...</div>
                    <div class="excel-loader-progress-container">
                        <div class="excel-loader-progress-bar">
                            <div class="excel-loader-progress-fill"></div>
                        </div>
                        <div class="excel-loader-progress-text">0%</div>
                    </div>
                    <div class="excel-loader-details"></div>
                </div>
            `;
            document.body.appendChild(container);
        }

        // Guardar referencias a elementos
        this.elements = {
            container: document.getElementById('excel-loader-ui'),
            spinner: document.querySelector('.excel-loader-spinner'),
            title: document.querySelector('.excel-loader-title'),
            message: document.querySelector('.excel-loader-message'),
            progressBar: document.querySelector('.excel-loader-progress-fill'),
            progressText: document.querySelector('.excel-loader-progress-text'),
            details: document.querySelector('.excel-loader-details')
        };

        this.isInitialized = true;
    },

    /**
     * Muestra el indicador de carga
     */
    show() {
        this.init();
        this.elements.container.classList.add('visible');
        console.log('👁️ Indicador de carga mostrado');
    },

    /**
     * Oculta el indicador de carga
     */
    hide() {
        if (!this.isInitialized) return;
        this.elements.container.classList.remove('visible');
        console.log('👁️ Indicador de carga ocultado');
    },

    /**
     * Actualiza el mensaje de estado
     * @param {string} message - Mensaje a mostrar
     */
    setMessage(message) {
        if (!this.isInitialized) return;
        this.elements.message.textContent = message;
    },

    /**
     * Actualiza el título
     * @param {string} title - Título a mostrar
     */
    setTitle(title) {
        if (!this.isInitialized) return;
        this.elements.title.textContent = title;
    },

    /**
     * Actualiza la barra de progreso
     * @param {number} percent - Porcentaje de progreso (0-100)
     */
    setProgress(percent) {
        if (!this.isInitialized) return;
        const clampedPercent = Math.max(0, Math.min(100, percent));
        this.elements.progressBar.style.width = `${clampedPercent}%`;
        this.elements.progressText.textContent = `${Math.round(clampedPercent)}%`;
    },

    /**
     * Actualiza los detalles técnicos
     * @param {string} details - Detalles a mostrar
     */
    setDetails(details) {
        if (!this.isInitialized) return;
        this.elements.details.textContent = details;
    },

    /**
     * Muestra información de reintento
     * @param {number} attempt - Número de intento actual
     * @param {number} maxAttempts - Número máximo de intentos
     * @param {number} delayMs - Delay hasta el próximo intento
     */
    showRetry(attempt, maxAttempts, delayMs) {
        this.setMessage(`Reintentando... (${attempt}/${maxAttempts})`);
        this.setDetails(`Próximo intento en ${(delayMs / 1000).toFixed(1)}s`);

        // Calcular progreso basado en intentos
        const progress = ((attempt - 1) / maxAttempts) * 100;
        this.setProgress(progress);
    },

    /**
     * Muestra mensaje de éxito
     * @param {string} message - Mensaje de éxito
     * @param {number} autoHideMs - Tiempo antes de ocultar automáticamente (0 = no ocultar)
     */
    showSuccess(message, autoHideMs = 1500) {
        this.setTitle('✅ Carga Exitosa');
        this.setMessage(message);
        this.setProgress(100);
        this.setDetails('');

        if (autoHideMs > 0) {
            setTimeout(() => this.hide(), autoHideMs);
        }
    },

    /**
     * Muestra mensaje de error
     * @param {string} title - Título del error
     * @param {string} message - Mensaje de error
     * @param {string} details - Detalles técnicos del error
     * @param {boolean} autoHide - Si debe ocultarse automáticamente
     */
    showError(title, message, details = '', autoHide = false) {
        this.show(); // Asegurar que sea visible si hay un error
        this.setTitle(`❌ ${title}`);
        this.setMessage(message);
        this.setDetails(details);
        this.setProgress(0);

        // Cambiar estilo a error
        this.elements.container.classList.add('error');

        if (autoHide) {
            setTimeout(() => {
                this.hide();
                this.elements.container.classList.remove('error');
            }, 5000);
        }
    },

    /**
     * Muestra advertencia de uso de caché
     * @param {Object} cacheInfo - Información del caché
     */
    showCacheWarning(cacheInfo) {
        const ageHours = Math.floor(cacheInfo.age / (60 * 60 * 1000));
        const ageDays = Math.floor(ageHours / 24);

        let ageText = '';
        if (ageDays > 0) {
            ageText = `${ageDays} día${ageDays > 1 ? 's' : ''}`;
        } else {
            ageText = `${ageHours} hora${ageHours > 1 ? 's' : ''}`;
        }

        this.setTitle('⚠️ Usando Caché Local');
        this.setMessage('No se pudo conectar al servidor');
        this.setDetails(`Usando datos guardados hace ${ageText}`);
        this.setProgress(100);

        this.elements.container.classList.add('warning');

        setTimeout(() => {
            this.hide();
            this.elements.container.classList.remove('warning');
        }, 3000);
    },

    /**
     * Actualiza el estado de verificación
     * @param {string} step - Paso de verificación actual
     */
    showVerification(step) {
        this.setMessage(`Verificando: ${step}`);
        this.setProgress(10);
    },

    /**
     * Muestra estado de descarga
     * @param {number} loaded - Bytes descargados
     * @param {number} total - Bytes totales
     */
    showDownload(loaded, total) {
        const percent = total > 0 ? (loaded / total) * 100 : 0;
        const loadedKB = (loaded / 1024).toFixed(2);
        const totalKB = (total / 1024).toFixed(2);

        this.setMessage('Descargando archivo...');
        this.setDetails(`${loadedKB} KB / ${totalKB} KB`);
        this.setProgress(percent);
    },

    /**
     * Limpia todos los estados visuales
     */
    reset() {
        if (!this.isInitialized) return;

        this.setTitle('Cargando Base de Datos');
        this.setMessage('Iniciando...');
        this.setProgress(0);
        this.setDetails('');
        this.elements.container.classList.remove('error', 'warning');
    }
};

// Exponer globalmente
window.LoaderUI = LoaderUI;

console.log('🎨 LoaderUI cargado');
